/**
 * Auth state, ported from grant_management and trimmed to sgokit's roles.
 *
 * The token lives in localStorage; `refreshUser` rehydrates the user from
 * `/accounts/auth/current_user/` on load. `isOrgAdmin` is what gates the admin
 * console — and because `capabilities` is tenant-scoped on the server, it is true
 * only on a subdomain the user actually administers.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import { authApi, AuthUser, TenantContext } from '../api/auth';

interface AuthContextType {
  user: AuthUser | null;
  token: string | null;
  loading: boolean;
  tenantContext: TenantContext | null;
  setAuthData: (user: AuthUser, token: string) => void;
  refreshUser: () => Promise<void>;
  logout: () => Promise<void>;
  isAdmin: boolean;
  isOrgAdmin: boolean;
  isSuperuser: boolean;
  isPartner: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [tenantContext, setTenantContext] = useState<TenantContext | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      localStorage.setItem('token', token);
    } else {
      localStorage.removeItem('token');
    }
  }, [token]);

  const refreshUser = useCallback(async () => {
    try {
      const data = await authApi.currentUser();
      setUser(data.user);
      setTenantContext(data.tenant_context ?? null);
    } catch {
      setUser(null);
      setTenantContext(null);
      setToken(null);
    }
  }, []);

  useEffect(() => {
    if (token) {
      refreshUser().finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [token, refreshUser]);

  const setAuthData = (userData: AuthUser, tokenData: string) => {
    setUser(userData);
    setToken(tokenData);
  };

  const logout = async () => {
    try {
      await authApi.logout();
    } catch {
      // Clear local state even if the server call fails.
    }
    setUser(null);
    setTenantContext(null);
    setToken(null);
  };

  const caps = user?.capabilities;
  const value: AuthContextType = {
    user,
    token,
    loading,
    tenantContext,
    setAuthData,
    refreshUser,
    logout,
    isAdmin: (caps?.is_org_admin || caps?.is_org_user || caps?.is_platform_admin) ?? false,
    isOrgAdmin: caps?.is_org_admin ?? false,
    isSuperuser: caps?.is_platform_admin ?? false,
    isPartner: caps?.is_partner ?? false,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
